import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { NavigationList, DropdownContainer, TabsWrapper, TabButton, CheckboxWrapper, ScheduleList, ScheduleItem, ScheduleItemHeader, ScheduleMeta, ScheduleTime, ScheduleTitle, EmptyState } from './styles';
import { Lang } from '@brg/api';
import { useTranslation } from 'react-i18next';
import { InfoItem } from './components/InfoItem';
import { Dropdown } from '@brg/ui';
import ArrowTopIconWithText from 'shared/icons/ArrowTopIconWithText';
import { DropdownBodyWrapper } from '../ProjectAnchorNavigate';
import styled from 'styled-components';
import useInstantScroll from 'shared/hooks/useSmoothScroll';
import { extractTimeFromISO, formatDate, getDayOfWeekLabel, isValidUrl } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox';

const HOMEWORK_SIGN = 'Домашняя работа';

const FullWidthDiv = styled.div`
    padding-right: 16px;
    > div {
        width: 100%;
    }

    .dropdown-button{
        position: relative;
        z-index: 999999;
    }
`;


export type JustHomeWorkT = {
    justHomeWork: boolean
}
type DataInfoT = {
    title: string;
    data: any;
}

type ScheduleLesson = {
    id: string;
    start?: string;
    end?: string;
    groupIds: string[];
    themeId?: string;
    lessonLink?: string;
    title?: Array<{ lang: string; name: string }>;
    status?: number;
    type?: number;
}

type ScheduleConfig = {
    lessons?: ScheduleLesson[];
    role?: 'student' | 'teacher' | 'jury' | 'none';
    isLoading?: boolean;
    showSchedule?: boolean;
    onShowScheduleChange?: (val: boolean) => void;
}

type PropsT = {
    info?: DataInfoT[];
    onHmVisible: boolean;
    anchors: Record<string, any[]>;
    onSortClick: (id: string) => void;
    hasHeader?: boolean;
    scheduleConfig?: ScheduleConfig;
    themeTitlesById?: Record<string, string>;
    uncheckedCountByTheme?: Record<string, number>;
}

export const CourseInfoList: FC<PropsT> = ({
    info,
    anchors,
    onHmVisible,
    onSortClick,
    hasHeader = false,
    scheduleConfig,
    themeTitlesById,
    uncheckedCountByTheme
}) => {
    const { t, i18n } = useTranslation();
    const navRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [activeHash, setActiveHash] = useState(() => window.location.hash.replace('#', ''));
    const [activeTab, setActiveTab] = useState<'content' | 'schedule'>('content');
    const [highlightedSlotId, setHighlightedSlotId] = useState<string | null>(null);
    const [activeScheduleSlotId, setActiveScheduleSlotId] = useState<string | null>(null);
    const { scrollToElement } = useInstantScroll({
        navbarHeight: 64,
        anchorMenuHeight: 49,
        extraOffset: -40,
        minDuration: 1500,
        maxDuration: 2000,
        scrollSpeed: 1.0,
    });

    const contentTabLabel = t('lms.lessonsEditor.contentTab', { defaultValue: 'Контент' });
    const scheduleTabLabel = t('lms.lessonsEditor.scheduleTab', { defaultValue: 'Расписание' });
    const showScheduleLabel = t('lms.lessonsEditor.showSchedule', { defaultValue: 'Показать расписание' });
    const noScheduleText = t('lms.lessonsEditor.noLessonsForModule', { defaultValue: 'Расписание для этого модуля ещё не настроено.' });

const handleClose = useCallback((ev: any) => {
    const target = ev.target as Element;

    if (target && typeof target.className === 'string' && target.className?.includes('baseVal')) {
        return;
    }

    if (navRef.current && navRef.current.contains(target)) {
        return;
    }

    setOpen(false);
}, []);

    useEffect(() => {
        window.addEventListener('click', handleClose);
        return () => {
            window.removeEventListener('click', handleClose);
        };
    }, [handleClose]);

    useEffect(() => {
        const updateHash = () => setActiveHash(window.location.hash.replace('#', ''));
        window.addEventListener('hashchange', updateHash);
        window.addEventListener('popstate', updateHash);
        return () => {
            window.removeEventListener('hashchange', updateHash);
            window.removeEventListener('popstate', updateHash);
        };
    }, []);

    const scheduleLessons = useMemo(() => scheduleConfig?.lessons || [], [scheduleConfig?.lessons]);
    const scheduleLoading = !!scheduleConfig?.isLoading;
    const hasScheduleFeature = useMemo(() => Boolean(scheduleConfig), [scheduleConfig]);
    const hasScheduleData = !scheduleLoading && scheduleLessons?.length > 0;
    const showSchedule = scheduleConfig?.showSchedule ?? false;
    const onShowScheduleChange = scheduleConfig?.onShowScheduleChange;

    const isContentEmpty = useMemo(() => {
        if (!info || info.length === 0) return true;
        const hasThemeStructure = info.find((i) => i?.title === 'theme_structure');
        if (!hasThemeStructure) return true;
        const structureData = hasThemeStructure?.data;
        if (!structureData || structureData.length === 0) return true;
        return false;
    }, [info]);

    useEffect(() => {
        if (!hasScheduleFeature) {
            setActiveTab('content');
            return;
        }
        if (isContentEmpty) {
            setActiveTab('schedule');
        }
    }, [hasScheduleFeature, isContentEmpty]);

    const resolveAnchorId = useCallback((themeId?: string) => {
        if (!themeId) return '';
        // anchors keyed by theme (longread) id -> array of anchor objects
        const themeAnchors = anchors?.[themeId] || [];
        if (!Array.isArray(themeAnchors) || themeAnchors.length === 0) {
            return '';
        }

        // prefer course_theme, else any with postId
        const preferred = themeAnchors.find((a: any) => a?.style === 'course_theme' && typeof a?.postId === 'string' && a.postId);
        const fallback = themeAnchors.find((a: any) => typeof a?.postId === 'string' && a.postId);
        const postId = preferred?.postId || fallback?.postId;
        return typeof postId === 'string' ? postId : '';
    }, [anchors]);

    const getThemeTitle = useCallback((themeId?: string, fallback?: Array<{ lang: string; name: string }>) => {
        if (themeId && themeTitlesById && typeof themeTitlesById[themeId] === 'string') {
            return themeTitlesById[themeId];
        }
        if (fallback) {
            const localized = Lang(fallback, i18n.language);
            if (typeof localized === 'string') {
                return localized;
            }
        }
        return '';
    }, [themeTitlesById, i18n.language]);

    const normalizeLocation = useCallback((value?: string) => {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        if (!trimmed) return '';

        if (isValidUrl(trimmed)) {
            try {
                const normalized = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
                const url = new URL(normalized);
                if (url.hostname) {
                    const parts = url.hostname.split('.');
                    if (parts.length >= 2) {
                        return parts.slice(-2).join('.');
                    }
                    return url.hostname;
                }
            } catch {
                return trimmed;
            }
        }
        return trimmed;
    }, []);

    const formattedScheduleLessons = useMemo(() => {
        if (!scheduleLessons || scheduleLessons.length === 0) return [];

        const lessonsCopy = [...scheduleLessons];
        lessonsCopy.sort((a, b) => {
            const getDate = (value?: string) => {
                if (!value || typeof value !== 'string') return Number.MAX_SAFE_INTEGER;
                const parsed = Date.parse(value);
                return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
            };
            return getDate(a?.start) - getDate(b?.start);
        });

        return lessonsCopy.map((lesson) => {
                const startDate = typeof lesson?.start === 'string' ? new Date(lesson.start) : null;

                const hasValidStart = !!startDate && !Number.isNaN(startDate?.getTime());
                const dayOfWeek = hasValidStart ? getDayOfWeekLabel(startDate, t) : '';
                const datePart = hasValidStart
                    ? dayOfWeek ? `${formatDate(startDate)} ${dayOfWeek}` : formatDate(startDate)
                    : '';

                const startTime = typeof lesson?.start === 'string' ? extractTimeFromISO(lesson.start) : '';
                const endTime = typeof lesson?.end === 'string' ? extractTimeFromISO(lesson.end) : '';
                let timePart = '';
                if (startTime && endTime) {
                    timePart = `${startTime}–${endTime}`;
                } else if (startTime) {
                    timePart = startTime;
                }

                const themeTitle = getThemeTitle(lesson?.themeId, lesson?.title);
                const hasTheme = !!themeTitle;
                const title = hasTheme ? themeTitle : t('project_edit.lms.no_topic', { defaultValue: 'Тема не назначена' });

                const location = normalizeLocation(lesson?.lessonLink);
                const metaParts: string[] = [];
                if (location) {
                    metaParts.push(location);
                }

                const anchorId = resolveAnchorId(lesson?.themeId);

                return {
                    slotId: lesson?.id || lesson?.themeId || '',
                    themeId: lesson?.themeId,
                    anchorId,
                    datePart,
                    timePart,
                    title,
                    isPlaceholder: !hasTheme,
                    metaParts,
                };
            });
    }, [scheduleLessons, getThemeTitle, t, normalizeLocation, resolveAnchorId]);

    const handleScheduleItemClick = useCallback((slotId: string, themeId?: string) => {
        // Устанавливаем активный элемент
        setActiveScheduleSlotId(slotId);
        
        const anchorId = resolveAnchorId(themeId);
        if (anchorId) {
            // trigger hashchange to force longread loader to fetch required theme
            if (window.location.hash !== `#${anchorId}`) {
                window.location.hash = `#${anchorId}`;
            }

            let attempts = 0;
            const maxAttempts = 15;
            const tryScroll = () => {
                attempts += 1;
                scrollToElement(anchorId, false);
                const el = document.querySelector(`[data-rbd-draggable-id="${anchorId}"], #${anchorId}, [data-post-id="${anchorId}"], [data-anchor="${anchorId}"]`);
                if (el) return;
                if (attempts < maxAttempts) {
                    setTimeout(tryScroll, 200);
                }
            };
            tryScroll();
            return;
        }

        if (slotId) {
            setHighlightedSlotId(slotId);
            setTimeout(() => {
                setHighlightedSlotId(null);
            }, 1500);
        }
    }, [resolveAnchorId, scrollToElement]);

    const renderContentList = useCallback((data: any[]) => {
        if (!data || data.length === 0) {
            return null;
        }

        let counter = 0;

        return data.map((item: any, idx: number) => {
            const lgId = item?.longreadId;
            const hasHomeWork = item?.isHomework;
            const isExam = item?.isExam;
            const localizedName = Lang(isExam ? item?.title : item?.name, i18n.language);

            // @ts-ignore
            let anchor = anchors[lgId]?.find((anchorItem) => {
                return (anchorItem.name || anchorItem.text) === (hasHomeWork ? HOMEWORK_SIGN : localizedName);
            });

            if (!anchor && anchors[lgId]?.length > 0 && !hasHomeWork) {
                anchor = anchors[lgId][0];
            }

            const effectivePostId = anchor?.postId || anchor?.id;

            if (!effectivePostId || effectivePostId === 'undefined') {
                return null;
            }

            if (String(effectivePostId).toLowerCase().includes('consultation')) {
                return null;
            }

            if (hasHomeWork && effectivePostId === `homework-header-${lgId}` && !effectivePostId.includes('homework-header-')) {
                return null;
            }

            const link = `${window.location.pathname}#${effectivePostId}`;
            const isActive = activeHash === effectivePostId;

            if (isExam) {
                return (
                    <InfoItem
                        key={item?.id + idx}
                        showOrderNumber={!onHmVisible}
                        name={localizedName}
                        order={counter}
                        href={link}
                        isActive={isActive}
                        isExam
                        postId={effectivePostId}
                    />
                );
            }

            if (!hasHomeWork || !onHmVisible) {
                counter = counter + 1;
            }

            return (
                <InfoItem
                    key={item?.id + idx}
                    showOrderNumber={!onHmVisible}
                    name={localizedName}
                    order={counter}
                    href={link}
                    isActive={isActive}
                    isHomeWork={hasHomeWork}
                    hasNewHomeWork={hasHomeWork && Boolean(uncheckedCountByTheme?.[lgId])}
                    postId={effectivePostId}
                />
            );
        });
    }, [anchors, activeHash, i18n.language, onHmVisible, onSortClick, t, uncheckedCountByTheme]);

    const navigationItems = useMemo(() => {
        if (info && info.length > 0) {
            return info;
        }
        if (hasScheduleFeature) {
            return [{ title: 'theme_structure', data: [] }];
        }
        return [];
    }, [info, hasScheduleFeature]);

    if (navigationItems.length === 0) {
        return null;
    }

    return (
        <NavigationList ref={navRef} hasHeader={hasHeader}>
            <DropdownContainer visible={open} className='dropdown-container'>
                <DropdownBodyWrapper visible={open}>
                    {navigationItems.map((infoItem) => {
                        const title = infoItem.title;
                        const targetTitle = title === 'theme_structure'
                            ? t('project_edit.lms.theme_structure', { defaultValue: 'Структура модуля' })
                            : t(`project_edit.lms.${title}`);
                        const data = infoItem.data || [];

                        return (
                            // @ts-ignore
                            <FullWidthDiv key={title} className='sdsdsdsd'>
                                <Dropdown
                                    visibility={open}
                                    maxWidth={280}
                                    maxHeight={350}
                                    hideArrow
                                    target={
                                        <ArrowTopIconWithText
                                            onClick={() => setOpen(!open)}
                                            fill={'#909090'}
                                            shrimpIcon
                                            text={targetTitle}
                                        />
                                    }
                                >
                                    <DropdownBodyWrapper visible={open}>
                                        {hasScheduleFeature && hasScheduleData && (
                                            <CheckboxWrapper>
                                                <CommonCheckbox
                                                    checked={showSchedule}
                                                    onClick={(ev) => {
                                                        ev.stopPropagation();
                                                        onShowScheduleChange?.(!showSchedule);
                                                    }}
                                                    label={showScheduleLabel}
                                                />
                                            </CheckboxWrapper>
                                        )}

                                        {hasScheduleFeature && (
                                            <TabsWrapper>
                                                <TabButton
                                                    active={activeTab === 'content'}
                                                    onClick={() => setActiveTab('content')}
                                                >
                                                    {contentTabLabel}
                                                </TabButton>
                                                <TabButton
                                                    active={activeTab === 'schedule'}
                                                    onClick={() => setActiveTab('schedule')}
                                                >
                                                    {scheduleTabLabel}
                                                </TabButton>
                                            </TabsWrapper>
                                        )}

                                        {activeTab === 'content' && renderContentList(data)}

                                        {activeTab === 'schedule' && (
                                            <>
                                                {scheduleLoading && (
                                                    <EmptyState>{t('text.loading')}</EmptyState>
                                                )}

                                                {!scheduleLoading && formattedScheduleLessons.length === 0 && (
                                                    <EmptyState>
                                                        {noScheduleText}
                                                    </EmptyState>
                                                )}

                                                {!scheduleLoading && formattedScheduleLessons.length > 0 && (() => {
                                                    return (
                                                    <ScheduleList>
                                                        {formattedScheduleLessons.map((lesson) => {
                                                            const isActive = lesson.slotId === activeScheduleSlotId;
                                                            return (
                                                            <ScheduleItem
                                                                key={lesson.slotId}
                                                                isPlaceholder={lesson.isPlaceholder}
                                                                isHighlighted={lesson.slotId === highlightedSlotId}
                                                                isActive={isActive}
                                                                onClick={() => handleScheduleItemClick(lesson.slotId, lesson.themeId)}
                                                            >
                                                                <ScheduleItemHeader>
                                                                    <ScheduleTime>
                                                                        {lesson.datePart}
                                                                        {lesson.timePart ? ` ${lesson.timePart}` : ''}
                                                                    </ScheduleTime>
                                                                    {lesson.metaParts.length > 0 && (
                                                                        <ScheduleMeta>
                                                                            {lesson.metaParts.join(', ')}
                                                                        </ScheduleMeta>
                                                                    )}
                                                                </ScheduleItemHeader>
                                                                <ScheduleTitle isPlaceholder={lesson.isPlaceholder} isActive={isActive}>
                                                                    {lesson.title}
                                                                </ScheduleTitle>
                                                            </ScheduleItem>
                                                            );
                                                        })}
                                                    </ScheduleList>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </DropdownBodyWrapper>
                                </Dropdown>
                            </FullWidthDiv>
                        );
                    })}
                </DropdownBodyWrapper>
            </DropdownContainer>
        </NavigationList>
    );
}
import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { NavigationList, DropdownContainer, TabsWrapper, TabButton, CheckboxWrapper, ScheduleList, ScheduleItem, ScheduleItemHeader, ScheduleMeta, ScheduleTime, ScheduleTitle, EmptyState } from './styles';
import { Lang } from '@brg/api';
import { useTranslation } from 'react-i18next';
import { InfoItem } from './components/InfoItem';
import { Dropdown } from '@brg/ui';
import ArrowTopIconWithText from 'shared/icons/ArrowTopIconWithText';
import { DropdownBodyWrapper } from '../ProjectAnchorNavigate';
import styled from 'styled-components';
import useInstantScroll from 'shared/hooks/useSmoothScroll';
import { extractTimeFromISO, formatDate, getDayOfWeekLabel, isValidUrl } from 'shared/pages/projectEdit/components/LMS/LessonsEditor/helpers';
import { CommonCheckbox } from 'shared/components/CommonCheckbox/CommonCheckbox';

const HOMEWORK_SIGN = 'Домашняя работа';

const FullWidthDiv = styled.div`
    padding-right: 16px;
    > div {
        width: 100%;
    }

    .dropdown-button{
        position: relative;
        z-index: 999999;
    }
`;


export type JustHomeWorkT = {
    justHomeWork: boolean
}
type DataInfoT = {
    title: string;
    data: any;
}

type ScheduleLesson = {
    id: string;
    start?: string;
    end?: string;
    groupIds: string[];
    themeId?: string;
    lessonLink?: string;
    title?: Array<{ lang: string; name: string }>;
    status?: number;
    type?: number;
}

type ScheduleConfig = {
    lessons?: ScheduleLesson[];
    role?: 'student' | 'teacher' | 'jury' | 'none';
    isLoading?: boolean;
    showSchedule?: boolean;
    onShowScheduleChange?: (val: boolean) => void;
}

type PropsT = {
    info?: DataInfoT[];
    onHmVisible: boolean;
    anchors: Record<string, any[]>;
    onSortClick: (id: string) => void;
    hasHeader?: boolean;
    scheduleConfig?: ScheduleConfig;
    themeTitlesById?: Record<string, string>;
    uncheckedCountByTheme?: Record<string, number>;
}

export const CourseInfoList: FC<PropsT> = ({
    info,
    anchors,
    onHmVisible,
    onSortClick,
    hasHeader = false,
    scheduleConfig,
    themeTitlesById,
    uncheckedCountByTheme
}) => {
    const { t, i18n } = useTranslation();
    const navRef = useRef<HTMLDivElement>(null);
    const [open, setOpen] = useState(false);
    const [activeHash, setActiveHash] = useState(() => window.location.hash.replace('#', ''));
    const [activeTab, setActiveTab] = useState<'content' | 'schedule'>('content');
    const [highlightedSlotId, setHighlightedSlotId] = useState<string | null>(null);
    const [activeScheduleSlotId, setActiveScheduleSlotId] = useState<string | null>(null);
    const { scrollToElement } = useInstantScroll({
        navbarHeight: 64,
        anchorMenuHeight: 49,
        extraOffset: -40,
        minDuration: 1500,
        maxDuration: 2000,
        scrollSpeed: 1.0,
    });

    const contentTabLabel = t('lms.lessonsEditor.contentTab', { defaultValue: 'Контент' });
    const scheduleTabLabel = t('lms.lessonsEditor.scheduleTab', { defaultValue: 'Расписание' });
    const showScheduleLabel = t('lms.lessonsEditor.showSchedule', { defaultValue: 'Показать расписание' });
    const noScheduleText = t('lms.lessonsEditor.noLessonsForModule', { defaultValue: 'Расписание для этого модуля ещё не настроено.' });

const handleClose = useCallback((ev: any) => {
    const target = ev.target as Element;

    if (target && typeof target.className === 'string' && target.className?.includes('baseVal')) {
        return;
    }

    if (navRef.current && navRef.current.contains(target)) {
        return;
    }

    setOpen(false);
}, []);

    useEffect(() => {
        window.addEventListener('click', handleClose);
        return () => {
            window.removeEventListener('click', handleClose);
        };
    }, [handleClose]);

    useEffect(() => {
        const updateHash = () => setActiveHash(window.location.hash.replace('#', ''));
        window.addEventListener('hashchange', updateHash);
        window.addEventListener('popstate', updateHash);
        return () => {
            window.removeEventListener('hashchange', updateHash);
            window.removeEventListener('popstate', updateHash);
        };
    }, []);

    const scheduleLessons = useMemo(() => scheduleConfig?.lessons || [], [scheduleConfig?.lessons]);
    const scheduleLoading = !!scheduleConfig?.isLoading;
    const hasScheduleFeature = useMemo(() => Boolean(scheduleConfig), [scheduleConfig]);
    const hasScheduleData = !scheduleLoading && scheduleLessons?.length > 0;
    const showSchedule = scheduleConfig?.showSchedule ?? false;
    const onShowScheduleChange = scheduleConfig?.onShowScheduleChange;

    const isContentEmpty = useMemo(() => {
        if (!info || info.length === 0) return true;
        const hasThemeStructure = info.find((i) => i?.title === 'theme_structure');
        if (!hasThemeStructure) return true;
        const structureData = hasThemeStructure?.data;
        if (!structureData || structureData.length === 0) return true;
        return false;
    }, [info]);

    useEffect(() => {
        if (!hasScheduleFeature) {
            setActiveTab('content');
            return;
        }
        if (isContentEmpty) {
            setActiveTab('schedule');
        }
    }, [hasScheduleFeature, isContentEmpty]);

    const resolveAnchorId = useCallback((themeId?: string) => {
        if (!themeId) return '';
        // anchors keyed by theme (longread) id -> array of anchor objects
        const themeAnchors = anchors?.[themeId] || [];
        if (!Array.isArray(themeAnchors) || themeAnchors.length === 0) {
            return '';
        }

        // prefer course_theme, else any with postId
        const preferred = themeAnchors.find((a: any) => a?.style === 'course_theme' && typeof a?.postId === 'string' && a.postId);
        const fallback = themeAnchors.find((a: any) => typeof a?.postId === 'string' && a.postId);
        const postId = preferred?.postId || fallback?.postId;
        return typeof postId === 'string' ? postId : '';
    }, [anchors]);

    const getThemeTitle = useCallback((themeId?: string, fallback?: Array<{ lang: string; name: string }>) => {
        if (themeId && themeTitlesById && typeof themeTitlesById[themeId] === 'string') {
            return themeTitlesById[themeId];
        }
        if (fallback) {
            const localized = Lang(fallback, i18n.language);
            if (typeof localized === 'string') {
                return localized;
            }
        }
        return '';
    }, [themeTitlesById, i18n.language]);

    const normalizeLocation = useCallback((value?: string) => {
        if (typeof value !== 'string') return '';
        const trimmed = value.trim();
        if (!trimmed) return '';

        if (isValidUrl(trimmed)) {
            try {
                const normalized = trimmed.startsWith('http') ? trimmed : `https://${trimmed}`;
                const url = new URL(normalized);
                if (url.hostname) {
                    const parts = url.hostname.split('.');
                    if (parts.length >= 2) {
                        return parts.slice(-2).join('.');
                    }
                    return url.hostname;
                }
            } catch {
                return trimmed;
            }
        }
        return trimmed;
    }, []);

    const formattedScheduleLessons = useMemo(() => {
        if (!scheduleLessons || scheduleLessons.length === 0) return [];

        const lessonsCopy = [...scheduleLessons];
        lessonsCopy.sort((a, b) => {
            const getDate = (value?: string) => {
                if (!value || typeof value !== 'string') return Number.MAX_SAFE_INTEGER;
                const parsed = Date.parse(value);
                return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
            };
            return getDate(a?.start) - getDate(b?.start);
        });

        return lessonsCopy.map((lesson) => {
                const startDate = typeof lesson?.start === 'string' ? new Date(lesson.start) : null;

                const hasValidStart = !!startDate && !Number.isNaN(startDate?.getTime());
                const dayOfWeek = hasValidStart ? getDayOfWeekLabel(startDate, t) : '';
                const datePart = hasValidStart
                    ? dayOfWeek ? `${formatDate(startDate)} ${dayOfWeek}` : formatDate(startDate)
                    : '';

                const startTime = typeof lesson?.start === 'string' ? extractTimeFromISO(lesson.start) : '';
                const endTime = typeof lesson?.end === 'string' ? extractTimeFromISO(lesson.end) : '';
                let timePart = '';
                if (startTime && endTime) {
                    timePart = `${startTime}–${endTime}`;
                } else if (startTime) {
                    timePart = startTime;
                }

                const themeTitle = getThemeTitle(lesson?.themeId, lesson?.title);
                const hasTheme = !!themeTitle;
                const title = hasTheme ? themeTitle : t('project_edit.lms.no_topic', { defaultValue: 'Тема не назначена' });

                const location = normalizeLocation(lesson?.lessonLink);
                const metaParts: string[] = [];
                if (location) {
                    metaParts.push(location);
                }

                const anchorId = resolveAnchorId(lesson?.themeId);

                return {
                    slotId: lesson?.id || lesson?.themeId || '',
                    themeId: lesson?.themeId,
                    anchorId,
                    datePart,
                    timePart,
                    title,
                    isPlaceholder: !hasTheme,
                    metaParts,
                };
            });
    }, [scheduleLessons, getThemeTitle, t, normalizeLocation, resolveAnchorId]);

    const handleScheduleItemClick = useCallback((slotId: string, themeId?: string) => {
        // Устанавливаем активный элемент
        setActiveScheduleSlotId(slotId);
        
        const anchorId = resolveAnchorId(themeId);
        if (anchorId) {
            // trigger hashchange to force longread loader to fetch required theme
            if (window.location.hash !== `#${anchorId}`) {
                window.location.hash = `#${anchorId}`;
            }

            let attempts = 0;
            const maxAttempts = 15;
            const tryScroll = () => {
                attempts += 1;
                scrollToElement(anchorId, false);
                const el = document.querySelector(`[data-rbd-draggable-id="${anchorId}"], #${anchorId}, [data-post-id="${anchorId}"], [data-anchor="${anchorId}"]`);
                if (el) return;
                if (attempts < maxAttempts) {
                    setTimeout(tryScroll, 200);
                }
            };
            tryScroll();
            return;
        }

        if (slotId) {
            setHighlightedSlotId(slotId);
            setTimeout(() => {
                setHighlightedSlotId(null);
            }, 1500);
        }
    }, [resolveAnchorId, scrollToElement]);

    const renderContentList = useCallback((data: any[]) => {
        if (!data || data.length === 0) {
            return null;
        }

        let counter = 0;

        return data.map((item: any, idx: number) => {
            const lgId = item?.longreadId;
            const hasHomeWork = item?.isHomework;
            const isExam = item?.isExam;
            const localizedName = Lang(isExam ? item?.title : item?.name, i18n.language);

            // @ts-ignore
            let anchor = anchors[lgId]?.find((anchorItem) => {
                return (anchorItem.name || anchorItem.text) === (hasHomeWork ? HOMEWORK_SIGN : localizedName);
            });

            if (!anchor && anchors[lgId]?.length > 0 && !hasHomeWork) {
                anchor = anchors[lgId][0];
            }

            const effectivePostId = anchor?.postId || anchor?.id;

            if (!effectivePostId || effectivePostId === 'undefined') {
                return null;
            }

            if (String(effectivePostId).toLowerCase().includes('consultation')) {
                return null;
            }

            if (hasHomeWork && effectivePostId === `homework-header-${lgId}` && !effectivePostId.includes('homework-header-')) {
                return null;
            }

            const link = `${window.location.pathname}#${effectivePostId}`;
            const isActive = activeHash === effectivePostId;

            if (isExam) {
                return (
                    <InfoItem
                        key={item?.id + idx}
                        showOrderNumber={!onHmVisible}
                        name={localizedName}
                        order={counter}
                        href={link}
                        isActive={isActive}
                        isExam
                        postId={effectivePostId}
                    />
                );
            }

            if (!hasHomeWork || !onHmVisible) {
                counter = counter + 1;
            }

            return (
                <InfoItem
                    key={item?.id + idx}
                    showOrderNumber={!onHmVisible}
                    name={localizedName}
                    order={counter}
                    href={link}
                    isActive={isActive}
                    isHomeWork={hasHomeWork}
                    hasNewHomeWork={hasHomeWork && Boolean(uncheckedCountByTheme?.[lgId])}
                    postId={effectivePostId}
                />
            );
        });
    }, [anchors, activeHash, i18n.language, onHmVisible, onSortClick, t, uncheckedCountByTheme]);

    const navigationItems = useMemo(() => {
        if (info && info.length > 0) {
            return info;
        }
        if (hasScheduleFeature) {
            return [{ title: 'theme_structure', data: [] }];
        }
        return [];
    }, [info, hasScheduleFeature]);

    if (navigationItems.length === 0) {
        return null;
    }

    return (
        <NavigationList ref={navRef} hasHeader={hasHeader}>
            <DropdownContainer visible={open} className='dropdown-container'>
                <DropdownBodyWrapper visible={open}>
                    {navigationItems.map((infoItem) => {
                        const title = infoItem.title;
                        const targetTitle = title === 'theme_structure'
                            ? t('project_edit.lms.theme_structure', { defaultValue: 'Структура модуля' })
                            : t(`project_edit.lms.${title}`);
                        const data = infoItem.data || [];

                        return (
                            // @ts-ignore
                            <FullWidthDiv key={title} className='sdsdsdsd'>
                                <Dropdown
                                    visibility={open}
                                    maxWidth={280}
                                    maxHeight={350}
                                    hideArrow
                                    target={
                                        <ArrowTopIconWithText
                                            onClick={() => setOpen(!open)}
                                            fill={'#909090'}
                                            shrimpIcon
                                            text={targetTitle}
                                        />
                                    }
                                >
                                    <DropdownBodyWrapper visible={open}>
                                        {hasScheduleFeature && hasScheduleData && (
                                            <CheckboxWrapper>
                                                <CommonCheckbox
                                                    checked={showSchedule}
                                                    onClick={(ev) => {
                                                        ev.stopPropagation();
                                                        onShowScheduleChange?.(!showSchedule);
                                                    }}
                                                    label={showScheduleLabel}
                                                />
                                            </CheckboxWrapper>
                                        )}

                                        {hasScheduleFeature && (
                                            <TabsWrapper>
                                                <TabButton
                                                    active={activeTab === 'content'}
                                                    onClick={() => setActiveTab('content')}
                                                >
                                                    {contentTabLabel}
                                                </TabButton>
                                                <TabButton
                                                    active={activeTab === 'schedule'}
                                                    onClick={() => setActiveTab('schedule')}
                                                >
                                                    {scheduleTabLabel}
                                                </TabButton>
                                            </TabsWrapper>
                                        )}

                                        {activeTab === 'content' && renderContentList(data)}

                                        {activeTab === 'schedule' && (
                                            <>
                                                {scheduleLoading && (
                                                    <EmptyState>{t('text.loading')}</EmptyState>
                                                )}

                                                {!scheduleLoading && formattedScheduleLessons.length === 0 && (
                                                    <EmptyState>
                                                        {noScheduleText}
                                                    </EmptyState>
                                                )}

                                                {!scheduleLoading && formattedScheduleLessons.length > 0 && (() => {
                                                    return (
                                                    <ScheduleList>
                                                        {formattedScheduleLessons.map((lesson) => {
                                                            const isActive = lesson.slotId === activeScheduleSlotId;
                                                            return (
                                                            <ScheduleItem
                                                                key={lesson.slotId}
                                                                isPlaceholder={lesson.isPlaceholder}
                                                                isHighlighted={lesson.slotId === highlightedSlotId}
                                                                isActive={isActive}
                                                                onClick={() => handleScheduleItemClick(lesson.slotId, lesson.themeId)}
                                                            >
                                                                <ScheduleItemHeader>
                                                                    <ScheduleTime>
                                                                        {lesson.datePart}
                                                                        {lesson.timePart ? ` ${lesson.timePart}` : ''}
                                                                    </ScheduleTime>
                                                                    {lesson.metaParts.length > 0 && (
                                                                        <ScheduleMeta>
                                                                            {lesson.metaParts.join(', ')}
                                                                        </ScheduleMeta>
                                                                    )}
                                                                </ScheduleItemHeader>
                                                                <ScheduleTitle isPlaceholder={lesson.isPlaceholder} isActive={isActive}>
                                                                    {lesson.title}
                                                                </ScheduleTitle>
                                                            </ScheduleItem>
                                                            );
                                                        })}
                                                    </ScheduleList>
                                                    );
                                                })()}
                                            </>
                                        )}
                                    </DropdownBodyWrapper>
                                </Dropdown>
                            </FullWidthDiv>
                        );
                    })}
                </DropdownBodyWrapper>
            </DropdownContainer>
        </NavigationList>
    );
}
